// ─────────────────────────────────────────────
//  OutstandingFixModal — the "Fix" screen behind a NEEDS YOU NOW row.
//
//  One job: list the ROOMS an outstanding item is about, show who is in each of
//  them now, offer a dropdown of people who can actually take it, and write the
//  placements. Then close.
//
//  ⚠️ THE CLASH IS NEVER RE-DERIVED. `outstandingItems` carries the conflict
//  object on `item.conflict`, and this file uses it (`roomsToReassign`) to decide
//  which rooms are in question. The warning and the fix must not be able to
//  disagree about what is wrong — that is the whole reason the item carries it.
//
//  ⚠️ THE ELIGIBILITY TRAP. A picker that offers everyone is worse than no
//  picker: it will cheerfully hand the same clash back, or manufacture a new one
//  in a third room, and the board ends up asserting two impossible things where
//  it used to assert one. So a candidate is offered for a room only if placing
//  them there produces NO double booking — and that is decided by running
//  `doubleBookedProviders` (the exact detector that raised the warning) over a
//  HYPOTHETICAL board with the pick applied. Re-implementing the overlap test
//  here would be a second opinion, and a second opinion is a future disagreement.
//  The hypothetical includes the picks already made for the OTHER rooms in this
//  same modal, so filling room B cannot quietly break room C.
//
//  ⚠️ NO ALARM COLOURS IN HERE. Red and blink belong to emergencies on this
//  board. This is calm work — a second alarm-coloured surface would spend the
//  budget that makes a real one readable. Weight, rails and words carry rank
//  instead, exactly as the panel's own greyscale-safe treatments do.
//
//  ⚠️ MDs ARE NOT DOUBLE-BOOKABLE, BY DESIGN. `doubleBooking.js` constrains the
//  AHP seat only: a supervising doctor covering four rooms at once is the normal
//  shape of this service. Applying the overlap filter to the MD picker would
//  offer an empty list on a healthy board, so the "no supervising MD" fix offers
//  every rostered doctor.
// ─────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import tokens from "../../theme/tokens";
import Modal from "../Modal";
import Button from "../Button";
import { useToast } from "../../context/ToastContext";
import { updateAssignmentRow, setRoomProvider } from "../../firebase/firestore";
import { doubleBookedProviders, roomsToReassign } from "../../domain/doubleBooking";
import { crnaSeatName } from "../../domain/timeline";

const { color, font, space, radius } = tokens;

const normName = (n) => String(n || "").trim().toLowerCase();

function inSet(collection, id) {
  if (!collection) return false;
  if (typeof collection.has === "function") return collection.has(id);
  if (Array.isArray(collection)) return collection.includes(id);
  return false;
}

// ── Which items are fixable ─────────────────────────────────────────────────
//
// A button that opens a screen offering nothing is worse than no button. An
// unacknowledged emergency needs a person to acknowledge it, not a placement;
// "loses cover before the case ends" and "short-staffed at 3 PM" are forecasts
// with no room to fill right now. Those get no button.
export function fixModeFor(item) {
  if (!item || !item.key) return null;
  const key = String(item.key);
  if (key.startsWith("double-booked:")) return item.conflict ? "double-booked" : null;
  if (key === "uncovered") return "uncovered";
  if (key === "no-supervising-md") return "no-md";
  return null;
}

export function seatFor(mode) {
  return mode === "no-md" ? "md" : "crna";
}

// ── The board, as rows we can both simulate on and write to ─────────────────
//
// `model.placed` carries who-and-when but no assignment id, and the write needs
// the id. The lanes carry both, and both come from the same `buildTimeline`
// call, so a row built here is the same case the warning was computed from.
//
// TWO KEYS PER ROW, deliberately:
//   · `rawKey`       — `ahpId || name`, byte-identical to what timeline.js puts
//                      on `placed.crnaKey`, so `conflict.key` matches it.
//   · `crnaKey`      — the same thing with a bare NAME resolved to a roster id
//                      where one is known. A sheet-imported case can name a
//                      person without linking them; left raw, placing that same
//                      person BY ID into another room would look like two
//                      different people to the detector and the fix screen would
//                      offer a fresh double booking. Canonicalising can only
//                      ever remove a candidate, never add one, so it fails safe.
export function caseRowsFromLanes(lanes = [], pool = []) {
  const idByName = new Map();
  for (const p of Array.isArray(pool) ? pool : []) {
    if (p && p.id && p.name) idByName.set(normName(p.name), p.id);
  }
  const out = [];
  for (const lane of Array.isArray(lanes) ? lanes : []) {
    if (!lane || lane.roomId == null) continue;
    for (const c of Array.isArray(lane.cases) ? lane.cases : []) {
      if (!c || !c.id) continue;
      // ⚠️ `crnaSeatName`, NOT `c.provider`. On an orienting row `provider` is
      // still the raw cell naming TWO people, so `rawKey` became a sentence
      // that matched no `conflict.key` and no roster id — the fix screen would
      // offer to move a person nobody else on the board is called.
      const ahpName = crnaSeatName(c);
      const rawKey = c.ahpId || ahpName || null;
      const canon = c.ahpId || (ahpName ? idByName.get(normName(ahpName)) || `name:${normName(ahpName)}` : null);
      out.push({
        id: c.id,
        roomId: lane.roomId,
        roomName: lane.roomName || String(lane.roomId),
        startMin: c.startMin,
        endMin: c.endMin,
        ahpId: c.ahpId || null,
        ahpName,
        attendingId: c.attendingId || null,
        attendingName: String(c.attending ?? c.attendingName ?? "").trim(),
        onCall: Boolean(c.onCall),
        rawKey,
        crnaKey: canon,
      });
    }
  }
  return out;
}

// The rooms this fix is about, each with the assignment rows it has to rewrite.
export function buildPlans(mode, item, caseRows, ctx = {}) {
  const { uncoveredRoomIds, missingMdRooms = [], keepRoomId = "" } = ctx;
  const nameOf = (roomId) =>
    caseRows.find((r) => r.roomId === roomId)?.roomName || String(roomId);

  if (mode === "double-booked") {
    const conflict = item?.conflict;
    if (!conflict || !keepRoomId) return [];
    return roomsToReassign(conflict, keepRoomId).map((roomId) => {
      // Every row this provider holds in the room moves, not just one: leaving a
      // second overlapping row behind would re-raise the identical warning the
      // moment the modal closed.
      const cases = caseRows.filter((r) => r.roomId === roomId && r.rawKey === conflict.key);
      return { roomId, roomName: nameOf(roomId), cases, nowText: conflict.name };
    });
  }

  if (mode === "uncovered") {
    const ids = [];
    for (const r of caseRows) {
      if (inSet(uncoveredRoomIds, r.roomId) && !ids.includes(r.roomId)) ids.push(r.roomId);
    }
    return ids.map((roomId) => {
      const rooms = caseRows.filter((r) => r.roomId === roomId);
      // "Uncovered" is raised off the ON-CALL row (domain/coverage.js), so that
      // is the seat to fill when the room has one.
      const onCall = rooms.filter((r) => r.onCall);
      const cases = onCall.length ? onCall : rooms;
      return {
        roomId,
        roomName: nameOf(roomId),
        cases,
        nowText: cases.find((c) => c.ahpName)?.ahpName || "Nobody",
      };
    });
  }

  if (mode === "no-md") {
    return (Array.isArray(missingMdRooms) ? missingMdRooms : [])
      .filter(Boolean)
      .map((r) => ({
        roomId: r.roomId,
        roomName: r.roomName || nameOf(r.roomId),
        cases: caseRows.filter((c) => c.roomId === r.roomId && !c.attendingName),
        nowText: "No supervising doctor",
      }))
      .filter((p) => p.roomId != null);
  }

  return [];
}

// Would putting `candidateId` into `plan`'s rows leave them in two rooms at once?
//
// Answered by the SHIPPED detector on a hypothetical board rather than by a
// local overlap test, so this screen and the warning cannot drift apart. The
// other rooms' current picks are applied too — otherwise the same free person
// could be offered for two overlapping rooms and the fix would manufacture the
// exact failure it was opened to clear.
export function wouldDoubleBook(caseRows, plans, picks, plan, candidateId) {
  if (!candidateId) return true;
  const overrides = new Map();
  for (const p of plans) {
    const chosen = p.roomId === plan.roomId ? candidateId : picks[p.roomId];
    if (!chosen) continue;
    for (const c of p.cases) overrides.set(c.id, chosen);
  }
  const hypothetical = caseRows.map((r) =>
    overrides.has(r.id) ? { ...r, crnaKey: overrides.get(r.id) } : r
  );
  return doubleBookedProviders(hypothetical).some((c) => c.key === candidateId);
}

export default function OutstandingFixModal({
  item,
  lanes = [],
  uncoveredRoomIds = null,
  missingMdRooms = [],
  crnaPool = [],
  doctorPool = [],
  onClose,
}) {
  const toast = useToast();
  const mode = fixModeFor(item);
  const seat = seatFor(mode);
  const pool = seat === "md" ? doctorPool : crnaPool;

  const [keepRoomId, setKeepRoomId] = useState("");
  const [picks, setPicks] = useState({});
  const [placed, setPlaced] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const caseRows = useMemo(() => caseRowsFromLanes(lanes, crnaPool), [lanes, crnaPool]);
  const plans = useMemo(
    () => buildPlans(mode, item, caseRows, { uncoveredRoomIds, missingMdRooms, keepRoomId }),
    [mode, item, caseRows, uncoveredRoomIds, missingMdRooms, keepRoomId]
  );

  const candidatesFor = (plan) => {
    const list = (Array.isArray(pool) ? pool : []).filter((p) => p && p.id);
    // The MD seat is not constrained to one room — see the header note.
    if (seat === "md") return list;
    return list.filter((p) => !wouldDoubleBook(caseRows, plans, picks, plan, p.id));
  };

  if (!item || !mode) return null;

  const conflict = item.conflict || null;
  const keepRooms =
    mode === "double-booked" && conflict
      ? (conflict.roomIds || []).map((id) => ({
          id,
          name: caseRows.find((r) => r.roomId === id)?.roomName || String(id),
        }))
      : [];

  // ⚠️ A PLAN WITH NO ROWS MUST NOT BE APPLIED. `item` is a snapshot taken when
  // the button was pressed, while `lanes` keeps streaming: by the time somebody
  // picks, the case this room was flagged for may already have moved or been
  // reassigned. Writing the ROOM DOC with no matching row behind it is precisely
  // the split that makes a covered room read as empty — so those rooms say what
  // happened and are excluded from the apply.
  const pending = plans.filter((p) => !placed[p.roomId]);
  const chosen = pending.filter((p) => picks[p.roomId] && p.cases.length > 0);
  const canApply = !busy && chosen.length > 0 && (mode !== "double-booked" || !!keepRoomId);
  const unfilled = pending.filter((p) => !picks[p.roomId]);

  const apply = async () => {
    setBusy(true);
    setReport(null);
    const ok = [];
    const failed = [];
    let firstError = "";
    for (const plan of chosen) {
      const pick = pool.find((p) => p && p.id === picks[plan.roomId]);
      if (!pick) continue;
      try {
        // EXACTLY what TimelineCaseModal's assignSeat does, per room: the row
        // AND the room doc, both, every time. RoomCard resolves
        // `asg.ahpName || room.providerName`, so writing one without the other
        // leaves the board and the room card disagreeing about who is in it —
        // and a covered room reading as empty is the failure this card exists
        // to prevent.
        for (const c of plan.cases) {
          await updateAssignmentRow(
            c.id,
            seat === "md"
              ? { attendingId: pick.id, attendingName: pick.name || "" }
              : { ahpId: pick.id, ahpName: pick.name || "" }
          );
        }
        await setRoomProvider(
          plan.roomId,
          seat === "md"
            ? { supervisingDoctorId: pick.id, supervisorName: pick.name || "" }
            : { providerId: pick.id, providerName: pick.name || "" }
        );
        ok.push(plan.roomName);
        setPlaced((prev) => ({ ...prev, [plan.roomId]: true }));
      } catch (e) {
        console.error("Outstanding fix write failed:", e);
        failed.push(plan.roomName);
        // VERBATIM. A rewritten message ("could not save") hides whether this
        // was a permission refusal, a stale row or a dropped connection, and
        // the person reading it is the one who has to decide what to do next.
        if (!firstError) firstError = e?.message || String(e);
      }
    }
    setBusy(false);

    // A HALF-APPLIED FIX MUST NOT CLAIM TO HAVE WORKED. Anything refused keeps
    // the modal open with both lists named; only a clean run closes.
    if (failed.length) {
      setReport({ ok, failed, error: firstError });
      return;
    }
    toast.success(
      ok.length === 1 ? `${ok[0]} placed.` : `${ok.length} rooms placed: ${ok.join(", ")}.`
    );
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Fix — ${item.label}`}
      maxWidth={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={apply} disabled={!canApply}>
            {busy ? "Placing…" : "Apply"}
          </Button>
        </>
      }
    >
      <div style={st.body}>
        {mode === "double-booked" && (
          <fieldset style={st.fieldset}>
            <legend style={st.legend}>
              Which room does {conflict?.name || "this provider"} keep?
            </legend>
            {keepRooms.map((r) => (
              <label key={r.id} style={st.keepRow}>
                <input
                  type="radio"
                  name="fix-keep-room"
                  value={r.id}
                  checked={keepRoomId === r.id}
                  onChange={() => { setKeepRoomId(r.id); setPicks({}); setReport(null); }}
                  disabled={busy}
                />
                <span style={st.keepName}>{r.name}</span>
              </label>
            ))}
            {!keepRoomId && (
              <p style={st.note} data-testid="fix-keep-prompt">
                Every other room needs somebody else. Choose the one they keep first.
              </p>
            )}
          </fieldset>
        )}

        {plans.length > 0 && (
          <div style={st.rooms}>
            {plans.map((plan) => {
              const options = candidatesFor(plan);
              const isPlaced = Boolean(placed[plan.roomId]);
              const selectId = `fix-room-${plan.roomId}`;
              return (
                <div key={plan.roomId} style={st.roomRow} data-testid={`fix-room-${plan.roomId}`}>
                  <div style={st.roomHead}>
                    <span style={st.roomName}>{plan.roomName}</span>
                    <span style={st.roomNow}>Now: {plan.nowText}</span>
                  </div>
                  {isPlaced ? (
                    <p style={st.placedNote}>Placed.</p>
                  ) : plan.cases.length === 0 ? (
                    <p style={st.none} data-testid={`fix-stale-${plan.roomId}`}>
                      This room's case has changed since the warning was raised. Close and
                      reopen the fix so it acts on what is on the board now.
                    </p>
                  ) : options.length === 0 ? (
                    // AN EMPTY DROPDOWN IS A DEAD END THAT LOOKS LIKE A BUG.
                    // Say why nobody is there instead.
                    <p style={st.none} data-testid={`fix-none-${plan.roomId}`}>
                      {seat === "md"
                        ? "No doctors on today's roster to assign."
                        : "Nobody on today's roster is free for this room's hours — everyone else is already in another room at the same time."}
                    </p>
                  ) : (
                    <>
                      <label htmlFor={selectId} style={st.label}>
                        {seat === "md" ? "Supervising doctor" : "Assign"}
                      </label>
                      <select
                        id={selectId}
                        style={st.select}
                        value={picks[plan.roomId] || ""}
                        disabled={busy}
                        onChange={(e) =>
                          setPicks((prev) => ({ ...prev, [plan.roomId]: e.target.value }))
                        }
                      >
                        <option value="">{seat === "md" ? "Pick a doctor…" : "Pick a CRNA / AA…"}</option>
                        {options.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {plans.length > 0 && unfilled.length > 0 && (
          <p style={st.note}>
            {unfilled.length} of {pending.length} rooms still have nobody.
            {mode === "double-booked" ? " Applying now will not clear the clash." : ""}
          </p>
        )}

        {report && (
          // Neutral by design: weight and words, no alarm hue. See the header.
          <div style={st.report} role="alert" data-testid="fix-report">
            <strong style={st.reportTitle}>Not fully applied</strong>
            <p style={st.reportLine}>
              {report.ok.length ? `Placed: ${report.ok.join(", ")}.` : "Placed: none."}
            </p>
            <p style={st.reportLine}>Not placed: {report.failed.join(", ")}.</p>
            <p style={st.reportError}>{report.error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

const st = {
  body: { display: "flex", flexDirection: "column", gap: space.md },
  fieldset: { border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px ${space.md}px`, margin: 0 },
  legend: { fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, textTransform: "uppercase", letterSpacing: ".5px", padding: `0 ${space.xs}px` },
  keepRow: { display: "flex", alignItems: "center", gap: space.sm, padding: "5px 0", fontSize: font.size.body, color: color.text.primary, cursor: "pointer" },
  keepName: { fontWeight: font.weight.semibold },
  rooms: { display: "flex", flexDirection: "column", gap: space.sm },
  roomRow: { display: "flex", flexDirection: "column", gap: space.xs, background: color.surface.raised, borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px` },
  roomHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: space.sm, flexWrap: "wrap" },
  roomName: { fontSize: font.size.body, fontWeight: font.weight.bold, color: color.text.primary },
  roomNow: { fontSize: font.size.label, color: color.text.secondary },
  label: { fontSize: font.size.label, fontWeight: font.weight.semibold, color: color.text.secondary },
  select: { width: "100%", padding: "8px 10px", borderRadius: radius.sm, border: `1px solid ${color.border.default}`, fontSize: font.size.body, fontFamily: "inherit", background: color.surface.card, color: color.text.primary, boxSizing: "border-box" },
  none: { margin: 0, fontSize: font.size.label, color: color.text.secondary, lineHeight: font.lineHeight.normal },
  placedNote: { margin: 0, fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary },
  note: { margin: 0, fontSize: font.size.label, color: color.text.secondary, lineHeight: font.lineHeight.normal },
  report: { borderLeft: `4px solid ${color.text.secondary}`, background: color.surface.base, borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px` },
  reportTitle: { display: "block", fontSize: font.size.body, color: color.text.primary },
  reportLine: { margin: `${space.xs}px 0 0`, fontSize: font.size.label, color: color.text.primary },
  reportError: { margin: `${space.sm}px 0 0`, fontSize: font.size.label, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: color.text.secondary, wordBreak: "break-word" },
};
